import { features } from '!/flags'
import {
  ErrorMessage,
  GradientButtonWithCorners,
  InlineButtonAdditional2,
  SignUpActionContainerSC,
  SignUpFormTemplate,
  SocialAppleButtonTemplate,
  SocialFacebookButtonTemplate,
  SocialGoogleButtonTemplate,
  Text,
  TitleLineDivider,
  Wrapper,
} from '@setplex/arcane'
import { useForm } from 'effector-forms'
import { useUnit } from 'effector-react'
import { type FormEvent } from 'react'
import { SignUpMethod } from '~/shared/analytics/index.h'
import { PATH } from '~/shared/constants/path'
import { addGoogleAnalyticsPrefix } from '~/shared/helpers'
import * as signInModel from '~/widgets/auth/sign-in/model'
import { buildSocialAuthUrl, useGetRedirectUrl } from '../../helper'
import { socialAuthStarted } from '../../model'
import {
  $canSubmit,
  $error,
  $isSignUpPopupHeld,
  resetErrorOnClick,
  signUpForm,
  signUpFx,
} from '../model'
import { SignUpFields } from './SignUpFields'
import { SignUpInfoBlock } from './SignUpInfoBlock'

interface SignUpFormProps {
  info: boolean
  onShowInfo: (value: boolean) => void
}

SignUpForm.displayName = 'SignUpForm'
export function SignUpForm({ info, onShowInfo }: SignUpFormProps) {
  const error = useUnit($error)
  const pending = useUnit(signUpFx.pending)
  const isSignUpPopupHeld = useUnit($isSignUpPopupHeld)
  const isSubmitting = pending || isSignUpPopupHeld
  const { submit, eachValid } = useForm(signUpForm)

  const canSubmit = useUnit($canSubmit)

  const facebookEnabled = useUnit(features.$facebookEnabled)
  const googleEnabled = useUnit(features.$googleEnabled)
  const appleEnabled = useUnit(features.$appleEnabled)

  const onSignUpSubmit = (e: FormEvent) => {
    e.preventDefault()
    submit()
  }

  const onOpenSignInPopup = () => {
    signInModel.tryToOpenSignInPopup()
  }

  const redirectUrl = useGetRedirectUrl()
  const onSocialAuth = useUnit(socialAuthStarted)

  if (info) {
    return (
      <SignUpFormTemplate autoComplete='off' noValidate>
        <SignUpInfoBlock />
      </SignUpFormTemplate>
    )
  }

  return (
    <SignUpFormTemplate
      onSubmit={onSignUpSubmit}
      autoComplete='off'
      noValidate
      onClick={resetErrorOnClick}
    >
      <Text
        text='Create Your Free Account'
        className='text--fonts-subtitle-xl-bold text--color-on-surface-1 page-title text--center'
      />
      <Text
        text='It’s FREE. No subscription required.'
        className='text--fonts-body-xs-medium text--color-on-surface-2 text--center'
      />
      <Wrapper className='auth-button-group-template wrapper--justify-content-center'>
        {facebookEnabled && (
          <SocialFacebookButtonTemplate
            href={buildSocialAuthUrl('facebook', { redirectUrl })}
            data-ignore-router
            onClick={() => onSocialAuth(SignUpMethod.Facebook)}
            classNameGA={addGoogleAnalyticsPrefix('sign-up-facebook')}
          />
        )}
        {appleEnabled && (
          <SocialAppleButtonTemplate
            href={buildSocialAuthUrl('apple', { redirectUrl })}
            data-ignore-router
            onClick={() => onSocialAuth(SignUpMethod.Apple)}
            classNameGA={addGoogleAnalyticsPrefix('sign-up-apple')}
          />
        )}
        {googleEnabled && (
          <SocialGoogleButtonTemplate
            href={buildSocialAuthUrl('google', { redirectUrl })}
            data-ignore-router
            onClick={() => onSocialAuth(SignUpMethod.Google)}
            classNameGA={addGoogleAnalyticsPrefix('sign-up-google')}
          />
        )}
      </Wrapper>
      <TitleLineDivider title='or' />

      <SignUpFields
        pending={isSubmitting}
        onShowInfo={() => onShowInfo(true)}
      />

      <SignUpActionContainerSC>
        <GradientButtonWithCorners
          type='submit'
          title='Sign Up'
          disabled={isSubmitting || !eachValid || Boolean(error) || !canSubmit}
          isLoading={isSubmitting}
        />

        <Wrapper className='wrapper--justify-content-center'>
          <Text
            className='text--fonts-body-xs-medium text--color-on-surface-2'
            text='Already have an account? &nbsp;&nbsp;'
          />
          <InlineButtonAdditional2
            title='Sign In'
            onClick={onOpenSignInPopup}
          />
        </Wrapper>

        <Wrapper className='wrapper--justify-content-center terms-links-text-block wrapper--column'>
          <Text
            className='text--fonts-body-xs-medium text--color-on-surface-2'
            text='By registering you agree to UVOtvs'
          />
          <div>
            <a href={PATH.TERMS_OF_USE} target='_blank' rel='noreferrer'>
              <Text
                className='text--fonts-body-xs-medium'
                text='Terms of Use'
              />
            </a>
            <Text
              text='&nbsp;and&nbsp;'
              className='text--fonts-body-xs-medium text--color-on-surface-2'
            />
            <a href={PATH.STATIC_PRIVACY} target='_blank' rel='noreferrer'>
              <Text
                className='text--fonts-body-xs-medium'
                text='Privacy Policy'
              />
            </a>
          </div>
        </Wrapper>
      </SignUpActionContainerSC>
      {error && (
        <ErrorMessage
          title={error}
          maxLines={0}
          resetError={resetErrorOnClick}
        />
      )}
    </SignUpFormTemplate>
  )
}
